import { useCallback, useMemo, useState } from 'react';

import { toast } from '@feathr/components';
import { useUser } from '@feathr/extender/state';
import { logError } from '@feathr/hooks';
import { validate } from '@feathr/rachis';

import { useTimeFrameSelect } from './useTimeFrameSelect';

export interface IDateRangeOption {
  id: string;
  name: string;
  value: [string | undefined, string | undefined];
}

export interface IExportModalValidationErrors {
  email?: string[];
  dateRange?: string[];
  exportName?: string[];
}

interface IResolvedDateRange {
  startDate: string | undefined;
  endDate: string | undefined;
}

export interface IUseExportModalOptions {
  /**
   * When provided, the hook tracks an export-name field initialized to this value
   * and blocks submit while empty. Omit to leave export-name handling out of the hook.
   */
  initialExportName?: string;
}

interface IUseExportModalReturn {
  email: string;
  emailHadFocus: boolean;
  exportName: string;
  exportNameHadFocus: boolean;
  customDateRange: [string | undefined, string | undefined];
  selectedDateRange: string;
  isLoading: boolean;
  isSubmitDisabled: boolean;
  validationErrors: IExportModalValidationErrors | undefined;
  dateRangeOptions: IDateRangeOption[];
  handleEmailBlur: () => void;
  handleChangeEmail: (newEmail?: string) => void;
  handleChangeExportName: (newName?: string) => void;
  handleExportNameBlur: () => void;
  handleDateRangeChange: (option: IDateRangeOption) => void;
  handleCustomDateRangeChange: (value: [string | undefined, string | undefined]) => void;
  handleExport: (
    exportFn: () => Promise<unknown>,
    onSuccess: () => void,
    t: (key: string, options?: Record<string, unknown>) => string,
  ) => Promise<void>;
  setIsLoading: (loading: boolean) => void;
  resetFormState: () => void;
  resolveDateRange: () => IResolvedDateRange;
}

/**
 * Shared hook for export modal functionality.
 * Handles email validation, date range selection, optional export-name field,
 * and common modal state.
 *
 * @param t - Translation function from useTranslation.
 * @param options - Optional configuration. When `initialExportName` is provided, the hook
 *                  tracks an export-name field and blocks submit while it is empty.
 * @returns Export modal state and handlers.
 *
 * @example
 * ```tsx
 * const { exportName, handleChangeExportName, isSubmitDisabled } = useExportModal(t, {
 *   initialExportName: 'My Group_2026-05-13',
 * });
 * ```
 */
export function useExportModal(
  t: (key: string) => string,
  options: IUseExportModalOptions = {},
): IUseExportModalReturn {
  const { initialExportName } = options;
  const trackExportName = initialExportName !== undefined;
  const user = useUser();
  const { options: timeFrameOptions } = useTimeFrameSelect();

  const [email, setEmail] = useState<string>(user.email);
  const [emailHadFocus, setEmailHadFocus] = useState<boolean>(false);
  const [exportName, setExportName] = useState<string>(initialExportName ?? '');
  const [exportNameHadFocus, setExportNameHadFocus] = useState<boolean>(false);
  const [customDateRange, setCustomDateRange] = useState<[string | undefined, string | undefined]>([
    undefined,
    undefined,
  ]);
  const [selectedDateRange, setSelectedDateRange] = useState<string>('all_time');
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const dateRangeOptions: IDateRangeOption[] = [
    { id: 'all_time', name: t('All time'), value: [undefined, undefined] },
    ...timeFrameOptions.map((opt) => ({
      id: opt.id,
      name: opt.name,
      value: opt.value as [string | undefined, string | undefined],
    })),
    { id: 'custom', name: t('Custom'), value: [undefined, undefined] },
  ];

  const handleEmailBlur = useCallback((): void => {
    setEmailHadFocus(true);
  }, []);

  const handleChangeEmail = useCallback((newEmail?: string): void => {
    setEmail(newEmail ?? '');
    setEmailHadFocus(true);
  }, []);

  const handleChangeExportName = useCallback((newName?: string): void => {
    setExportName(newName ?? '');
    setExportNameHadFocus(true);
  }, []);

  const handleExportNameBlur = useCallback((): void => {
    setExportNameHadFocus(true);
  }, []);

  const handleDateRangeChange = useCallback((option: IDateRangeOption): void => {
    setSelectedDateRange(option.id);
  }, []);

  const handleCustomDateRangeChange = useCallback(
    (value: [string | undefined, string | undefined]): void => {
      setCustomDateRange(value);
    },
    [],
  );

  const resetFormState = useCallback((): void => {
    setEmail(user.email);
    setEmailHadFocus(false);
    setExportName(initialExportName ?? '');
    setExportNameHadFocus(false);
    setCustomDateRange([undefined, undefined]);
    setSelectedDateRange('all_time');
  }, [user.email, initialExportName]);

  const resolveDateRange = useCallback((): IResolvedDateRange => {
    if (selectedDateRange === 'all_time') {
      return { startDate: undefined, endDate: undefined };
    }

    if (selectedDateRange === 'custom') {
      return { startDate: customDateRange[0], endDate: customDateRange[1] };
    }

    const selectedOption = timeFrameOptions.find((opt) => opt.id === selectedDateRange);
    if (selectedOption?.value && Array.isArray(selectedOption.value)) {
      return { startDate: selectedOption.value[0], endDate: selectedOption.value[1] };
    }

    return { startDate: undefined, endDate: undefined };
  }, [selectedDateRange, customDateRange, timeFrameOptions]);

  const validateForm = useCallback((): IExportModalValidationErrors | undefined => {
    const emailConstraints = {
      presence: { allowEmpty: false, message: '^Email address cannot be empty.' },
      email: { message: '^Email address is invalid.' },
    };

    const baseErrors = validate({ email }, { email: emailConstraints }) ?? {};

    const errors: IExportModalValidationErrors = {};
    if (baseErrors.email) {
      errors.email = baseErrors.email;
    }

    if (trackExportName && exportName.trim().length === 0) {
      errors.exportName = ['Export name cannot be empty.'];
    }

    if (selectedDateRange === 'custom') {
      const startDate = customDateRange[0];
      const endDate = customDateRange[1];
      const today = new Date().toISOString().split('T')[0];

      if (!startDate || !endDate) {
        errors.dateRange = ['^Both start and end dates are required for custom range.'];
      } else if (startDate > endDate) {
        errors.dateRange = ['^Start date must be before end date.'];
      } else if (startDate > today || endDate > today) {
        errors.dateRange = ['^Dates cannot be in the future.'];
      }
    }

    return Object.keys(errors).length > 0 ? errors : undefined;
  }, [email, exportName, trackExportName, selectedDateRange, customDateRange]);

  const validationErrors = validateForm();

  const isSubmitDisabled = useMemo(
    () => isLoading || validationErrors !== undefined,
    [isLoading, validationErrors],
  );

  const handleExport = useCallback(
    async (
      exportFn: () => Promise<unknown>,
      onSuccess: () => void,
      t: (key: string, options?: Record<string, unknown>) => string,
    ): Promise<void> => {
      try {
        setIsLoading(true);
        await exportFn();
        toast(t('Started export. Check your email.'), {
          type: 'success',
        });
        onSuccess();
      } catch (error) {
        logError({
          message: 'Export request failed',
          level: 'error',
          extra: { error: error instanceof Error ? error.message : String(error) },
        });
        toast(t("Couldn't export data. Try again."), { type: 'error' });
      } finally {
        setIsLoading(false);
      }
    },
    [],
  );

  return {
    email,
    emailHadFocus,
    exportName,
    exportNameHadFocus,
    customDateRange,
    selectedDateRange,
    isLoading,
    isSubmitDisabled,
    validationErrors,
    dateRangeOptions,
    handleEmailBlur,
    handleChangeEmail,
    handleChangeExportName,
    handleExportNameBlur,
    handleDateRangeChange,
    handleCustomDateRangeChange,
    handleExport,
    setIsLoading,
    resetFormState,
    resolveDateRange,
  };
}
